// ---------------------------------------------------------------------------
// Mizu AI - the calculations behind the Operational Intelligence tab.
//
// Ported from the MizuWatch 3.3 design (public/mizuwatch-2.0), which
// built them into the page with innerHTML. Here they are plain functions of
// the feed payloads, so the React tab and the original produce the same
// figures from the same data, and each can be checked on its own.
// ---------------------------------------------------------------------------

// ── Number and text formatting ─────────────────────────────────────────────

// Feed values arrive as display strings - "$1,234.56", "12,000 Gallons".
export function parseNumberText(value) {
  const parsed = parseFloat(
    String(value || "")
      .replace(/,/g, "")
      .replace(/[^0-9.-]/g, ""),
  );
  return Number.isFinite(parsed) ? parsed : 0;
}

export const formatCurrency = (value) => `$${Math.round(value).toLocaleString()}`;

export function formatCompactCurrency(value) {
  const amount = Math.round(Number(value) || 0);
  if (amount >= 1000000) return `$${Math.round(amount / 1000000).toLocaleString()}M`;
  if (amount >= 1000) return `$${Math.round(amount / 1000).toLocaleString()}K`;
  return formatCurrency(amount);
}

export function formatGallons(gallons) {
  const value = parseNumberText(gallons);
  if (value >= 1000) return `${Math.round(value / 1000).toLocaleString()} KGAL`;
  return `${value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 3,
  })} GAL`;
}

// "12,000 Gallons lost" -> "12 KGAL lost", leaving "Gallons/hour" rates alone.
export const formatGallonsText = (value) =>
  String(value ?? "").replace(/([\d,]+(?:\.\d+)?)\s*Gallons(?!\/hour)/gi, (_, gallons) =>
    formatGallons(gallons),
  );

// ── Flow health: leak, backflow, no recent flow ─────────────────────────────

const FLOW_ID_KEY = {
  leak: "leak_meter",
  backflow: "backflow_meter",
  no_recent_flow: "no_recent_flow_meter",
};

const FLOW_MESSAGE_KEY = {
  leak: "leak_message",
  backflow: "backflow_message",
  no_recent_flow: "no_recent_message",
};

export function getFlowMeterIds(flowHealth, type) {
  const ids = flowHealth && Array.isArray(flowHealth[FLOW_ID_KEY[type]])
    ? flowHealth[FLOW_ID_KEY[type]]
    : [];
  return ids.map(String);
}

// A meter listed as leaking only counts once it carries the message that
// describes the leak - the id list and the detail rows are separate exports.
export function getFlowRecords(flowHealth, type) {
  const ids = new Set(getFlowMeterIds(flowHealth, type));
  const rows = flowHealth && Array.isArray(flowHealth.flow_health_data)
    ? flowHealth.flow_health_data
    : [];
  return rows.filter((row) => ids.has(String(row.meter_id)) && row[FLOW_MESSAGE_KEY[type]]);
}

export function getLeakGallonsPerHour(record) {
  const match = String(record?.leak_message || "").match(
    /Leak Detected:\s*([\d,]+(?:\.\d+)?)\s*Gallons\/hour/i,
  );
  return match ? parseNumberText(match[1]) : parseNumberText(record?.leak_rate);
}

// "Backflow detected: 18 gal in the last 30 days" -> 18. The live API writes
// "gal" / "kgal" where the export wrote "Gallons".
export function getBackflowGallons(record) {
  const match = String(record?.backflow_message || "").match(
    /Backflow detected:\s*([\d,]+(?:\.\d+)?)\s*(kgal|gal|Gallons)/i,
  );
  if (!match) return 0;
  return parseNumberText(match[1]) * (match[2].toLowerCase() === "kgal" ? 1000 : 1);
}

// Leaks by money lost, backflow by volume - the order a crew should go in.
export function sortFlowRecords(type, records) {
  if (type === "leak") {
    return [...records].sort(
      (a, b) => parseNumberText(b.total_price_for_loss) - parseNumberText(a.total_price_for_loss),
    );
  }
  if (type === "backflow") {
    return [...records].sort((a, b) => getBackflowGallons(b) - getBackflowGallons(a));
  }
  return records;
}

// "... was on 2026-08-02  . ..." -> the date split out so it can be highlighted.
export function splitNoRecentMessage(message) {
  const text = String(message || "");
  const marker = "was on ";
  const index = text.indexOf(marker);
  if (index === -1) return { before: text, date: "", after: "" };
  const dateStart = index + marker.length;
  const dateEnd = text.indexOf("  .", dateStart);
  if (dateEnd === -1) return { before: text, date: "", after: "" };
  return {
    before: text.slice(0, dateStart),
    date: text.slice(dateStart, dateEnd),
    after: text.slice(dateEnd),
  };
}

// ── Anomalies ────────────────────────────────────────────────────────────────

// Every anomaly row, tagged with its category, biggest peak first.
export function getAnomalyRecords(anomaly) {
  const records = [];
  (anomaly?.category_breakdown || []).forEach((category) => {
    const rows = Array.isArray(category.anomaly_data?.data) ? category.anomaly_data.data : [];
    rows.forEach((row) => {
      if (!row || row.meter_id == null) return;
      records.push({ ...row, category_name: category.category_name || "Anomaly" });
    });
  });
  return records.sort((a, b) => parseNumberText(b.highest_flow) - parseNumberText(a.highest_flow));
}

// Unique meter ids, first-seen order - a meter can sit in several categories.
export function getAnomalyMeterIds(anomaly) {
  const seen = new Set();
  (anomaly?.category_breakdown || []).forEach((category) => {
    const rows = Array.isArray(category.anomaly_data?.data) ? category.anomaly_data.data : [];
    rows.forEach((row) => {
      if (row && row.meter_id != null) seen.add(String(row.meter_id));
    });
  });
  return [...seen];
}

// ── Where things are ─────────────────────────────────────────────────────────

// A meter's point on the map, [lng, lat], or null when it has none. A meter
// with no location comes back with null coordinates, which Number() reads as
// 0: it was drawn at 0, 0 off West Africa, and framing a set that held one
// zoomed out across the Atlantic. Missing, zero and out-of-range coordinates
// are no point.
export function meterPoint(meter) {
  const lat = Number(meter?.location_latitude);
  const lng = Number(meter?.location_longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat === 0 || lng === 0) return null;
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null;
  return [lng, lat];
}

// The key the map groups the meters at one point by.
export const pointKey = ([lng, lat]) => `${lat.toFixed(6)},${lng.toFixed(6)}`;

// Meter id -> its point's key, for the meters the map can draw. A meter listed
// more than once keeps its first point.
export function meterPointKeys(meters) {
  const keys = new Map();
  (Array.isArray(meters) ? meters : []).forEach((meter) => {
    const id = String(meter.meter_id);
    const point = meterPoint(meter);
    if (point && !keys.has(id)) keys.set(id, pointKey(point));
  });
  return keys;
}

// "leak", "leak and anomaly", "leak, anomaly and backflow".
export const listText = (items) =>
  items.length > 1 ? `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}` : items[0] || "";

/**
 * The flagged meters (counts.flagged: a leak, an anomaly or backflow) and the
 * ones the map can show - what Hotspots counts as flagged sites and "Locate on
 * map" rings. `metersStatus` is the meter list's feed ("ready", "failed" or
 * "loading"), `pointKeys` its meters' points (meterPointKeys).
 *
 *   { status: "loading", waiting }  the feeds still on their way, by name
 *   { status: "failed", failed }    the feeds that never answered
 *   { status: "ready", ids, located, locations }
 *                                   every flagged meter, those with a map
 *                                   point, and how many points they are at
 */
export function flaggedOnMap(counts, metersStatus, pointKeys) {
  const feeds = [
    ["leak", counts.leak.status],
    ["anomaly", counts.anomaly.status],
    ["backflow", counts.backflow.status],
    ["meter location", metersStatus],
  ];
  const named = (status) => feeds.filter((feed) => feed[1] === status).map((feed) => feed[0]);
  if (named("loading").length) return { status: "loading", waiting: named("loading") };
  if (named("failed").length) return { status: "failed", failed: named("failed") };
  const ids = counts.flagged.ids;
  const located = ids.filter((id) => pointKeys.has(id));
  return { status: "ready", ids, located, locations: new Set(located.map((id) => pointKeys.get(id))).size };
}

export function buildAddressLookup(meters) {
  const lookup = new Map();
  (Array.isArray(meters) ? meters : []).forEach((meter) => {
    const id = String(meter.meter_id);
    if (!lookup.has(id) && meter.location_address) lookup.set(id, meter.location_address);
  });
  return (meterId) => lookup.get(String(meterId)) || "meter location";
}

// "12345 LOCKWOOD DR-DOM, HOUSTON" -> "LOCKWOOD DR"
export function streetOfAddress(address) {
  let street = String(address || "").split(",")[0].trim();
  street = street.replace(/^[\d/\- ]+/, "");
  street = street.replace(/-[A-Z0-9 ]+$/, "").trim();
  return street;
}

export const titleCaseStreet = (street) =>
  String(street || "")
    .split(/\s+/)
    .map((word) =>
      word.length <= 1 ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(" ");

// ── Leak hotspots ────────────────────────────────────────────────────────────
//
// Leaks are grouped by where they are, not by the street their address names:
// two leaks within the cluster distance of each other are one group, and a
// leak that close to any leak of a group joins it (single linkage). A run of
// leaks along one main stays one group; two leaks far apart on the same
// street are not grouped unless leaks between them link them. No district or
// street is treated specially.

// The cluster distance, in metres: VITE_LEAK_CLUSTER_DISTANCE_M at build time,
// 200 when that is unset or not a positive number.
const configuredDistance = Number(import.meta.env.VITE_LEAK_CLUSTER_DISTANCE_M);
export const LEAK_CLUSTER_DISTANCE_M =
  Number.isFinite(configuredDistance) && configuredDistance > 0 ? configuredDistance : 200;

// Great-circle distance in metres between two [lng, lat] points.
export function distanceM([lng1, lat1], [lng2, lat2]) {
  const rad = Math.PI / 180;
  const a =
    Math.sin(((lat2 - lat1) * rad) / 2) ** 2 +
    Math.cos(lat1 * rad) * Math.cos(lat2 * rad) * Math.sin(((lng2 - lng1) * rad) / 2) ** 2;
  return 2 * 6371008.8 * Math.asin(Math.min(1, Math.sqrt(a)));
}

// "12345 LOCKWOOD DR-DOM, HOUSTON" -> "12345 LOCKWOOD DR": the address's first
// part, without the service suffix (-DOM, -NPW). Only a suffix with no space
// in it: "123-125 MAIN ST" keeps its house numbers.
const addressLine = (address) =>
  String(address || "").split(",")[0].trim().replace(/-[A-Z0-9]+$/i, "").trim();

// An address as a place to match on - the same place whatever its case or
// punctuation. "" for none.
const placeOf = (address) => addressLine(address).toUpperCase().replace(/[^A-Z0-9]+/g, " ").trim();

/**
 * Each leaking meter once (a leak can have a row per register), with where it
 * is: `point`, [lng, lat] - its own in the meter list; else, when the meter is
 * not listed or listed without coordinates, a listed meter's at the leak
 * record's location_id, or at the same address - and `address`, the meter
 * list's or, failing that, the leak record's service_address. A meter listed
 * twice takes its first point and address.
 */
export function locateLeaks(leakIds, flowHealth, meters) {
  const listed = new Map();
  const atLocation = new Map();
  const atPlace = new Map();
  (Array.isArray(meters) ? meters : []).forEach((meter) => {
    const id = String(meter.meter_id);
    const point = meterPoint(meter);
    const address = String(meter.location_address || "").trim();
    const known = listed.get(id);
    listed.set(id, { point: known?.point || point, address: known?.address || address });
    if (!point) return;
    const location = meter.location_id == null ? "" : String(meter.location_id);
    if (location && !atLocation.has(location)) atLocation.set(location, point);
    const place = placeOf(address);
    if (place && !atPlace.has(place)) atPlace.set(place, point);
  });
  const records = new Map();
  getFlowRecords(flowHealth, "leak").forEach((row) => {
    const id = String(row.meter_id);
    const known = records.get(id);
    records.set(id, {
      address: known?.address || String(row.service_address || "").trim(),
      location: known?.location || (row.location_id == null ? "" : String(row.location_id)),
    });
  });
  return [...new Set(leakIds.map(String))].map((id) => {
    const meter = listed.get(id) || {};
    const record = records.get(id) || {};
    const address = meter.address || record.address || "";
    const point =
      meter.point ||
      (record.location && atLocation.get(record.location)) ||
      (address && atPlace.get(placeOf(address))) ||
      (record.address && atPlace.get(placeOf(record.address))) ||
      null;
    return { id, point, address, place: placeOf(address) };
  });
}

/**
 * The leaks (locateLeaks) in groups. Two are linked when both have a point and
 * are within `distance` metres of each other, or - one without a point - when
 * they are at the same address. Returns `clusters`, the groups of two or more,
 * largest first: their leaks' `ids`, `span` (the metres between the two
 * farthest apart by point), `street` (the one most of their addresses name - a
 * label only) and `address` (theirs when all are at one). With `total`, the
 * leaks looked at; `located`, those with a point or an address; and
 * `byAddress`, those with an address only.
 */
export function clusterLeaks(leaks, distance = LEAK_CLUSTER_DISTANCE_M) {
  const parent = leaks.map((_, index) => index);
  const root = (index) => (parent[index] === index ? index : (parent[index] = root(parent[index])));
  leaks.forEach((a, i) => {
    for (let j = i + 1; j < leaks.length; j += 1) {
      const b = leaks[j];
      const linked = a.point && b.point ? distanceM(a.point, b.point) <= distance : Boolean(a.place) && a.place === b.place;
      if (linked) parent[root(j)] = root(i);
    }
  });
  const groups = new Map();
  leaks.forEach((leak, index) => {
    const key = root(index);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(leak);
  });
  const clusters = [...groups.values()]
    .filter((members) => members.length > 1)
    .map((members) => {
      const points = members.filter((leak) => leak.point).map((leak) => leak.point);
      let span = 0;
      points.forEach((point, i) => {
        for (let j = i + 1; j < points.length; j += 1) span = Math.max(span, distanceM(point, points[j]));
      });
      const streets = new Map();
      members.forEach((leak) => {
        const street = streetOfAddress(leak.address);
        if (street) streets.set(street, (streets.get(street) || 0) + 1);
      });
      let street = "";
      streets.forEach((count, name) => {
        if (!street || count > streets.get(street)) street = name;
      });
      const onePlace = members[0].place && members.every((leak) => leak.place === members[0].place);
      return {
        ids: members.map((leak) => leak.id),
        count: members.length,
        span,
        street,
        address: onePlace ? addressLine(members[0].address) : "",
      };
    })
    .sort((a, b) => b.count - a.count || a.span - b.span);
  const located = leaks.filter((leak) => leak.point || leak.place).length;
  const byAddress = leaks.filter((leak) => !leak.point && leak.place).length;
  return { distance, total: leaks.length, located, byAddress, clusters };
}

// The leaks' clusters, from the feeds (locateLeaks, clusterLeaks).
export const leakClusters = (leakIds, flowHealth, meters, distance) =>
  clusterLeaks(locateLeaks(leakIds, flowHealth, meters), distance);

// 123 -> "130 m", 1234 -> "1.3 km": rounded up, so "within" it stays true.
export function formatSpan(metres) {
  const up = Math.ceil(metres / 10) * 10;
  return up < 1000 ? `${up} m` : `${(Math.ceil(metres / 100) / 10).toFixed(1)} km`;
}

// The cluster distance as a bound - rounded down, so "none within" it stays
// true.
const formatDistance = (metres) =>
  metres < 1000 ? `${Math.floor(metres)} m` : `${Math.floor(metres / 100) / 10} km`;

// Where a cluster is: "within 130 m of each other near Lockwood Dr", or "at
// 12345 Lockwood Dr" when its leaks are at one place.
export function clusterWhere(cluster) {
  if (cluster.span < 1) {
    return cluster.address ? `at ${titleCaseStreet(cluster.address)}` : "at one location";
  }
  const near = cluster.street ? ` near ${titleCaseStreet(cluster.street)}` : "";
  return `within ${formatSpan(cluster.span)} of each other${near}`;
}

// What the leaks show when none cluster, for those that could be placed -
// "of the 4 with a map location, none is within 200 m of another". Leaks
// known only by address were compared by address, not distance, so they are
// said apart. "" when none could be placed.
export function unclusteredText(groups) {
  const withPoint = groups.located - groups.byAddress;
  const parts = [];
  if (withPoint) {
    parts.push(`of the ${withPoint} with a map location, none is within ${formatDistance(groups.distance)} of another`);
  }
  if (groups.byAddress) {
    const n = groups.byAddress;
    parts.push(
      n === 1
        ? `${withPoint ? "1 more has" : "the 1 placed has"} only an address, so its distance from the others is unknown`
        : `${withPoint ? `${n} more have` : `the ${n} placed have`} only an address - none shared - so their distances are unknown`,
    );
  }
  return parts.join("; ");
}

export function getAlertUnionMeterIds(flowHealth, anomaly) {
  return [
    ...new Set([
      ...getFlowMeterIds(flowHealth, "leak"),
      ...getAnomalyMeterIds(anomaly),
      ...getFlowMeterIds(flowHealth, "backflow"),
    ]),
  ];
}

// ── System health ────────────────────────────────────────────────────────────

// A layer's percentage, or NaN when the feed has none - the feed writes a
// missing one as null, which Number() would read as 0%.
const layerPercent = (value) => (value == null || value === "" ? NaN : Number(value));

// The weakest of connectivity, data quality and device health.
export function getWeakestHealthLayer(systemHealth, systemHealthMeters) {
  const data = systemHealth?.data || {};
  const comm = data.device_comm_status || {};
  const quality = data.data_status || {};
  const meters = systemHealthMeters || {};
  const layers = [
    { name: "Connectivity", pct: layerPercent(comm.comm_status), ids: meters.communication_meter || [] },
    { name: "Data quality", pct: layerPercent(quality.good_percentage), ids: meters.data_meter || [] },
    { name: "Device health", pct: layerPercent(comm.device_status), ids: meters.device_meter || [] },
  ]
    .map((layer) => ({ ...layer, ids: layer.ids.map(String), meters: layer.ids.length }))
    .filter((layer) => Number.isFinite(layer.pct));
  if (!layers.length) return null;
  return layers.reduce((worst, layer) => (layer.pct < worst.pct ? layer : worst));
}

// ── Demand outlook ───────────────────────────────────────────────────────────

// The district's usage over the last seven days (feeds.usage, from
// apis/system_usage) - what the coming week is expected to draw at the same
// rate. null when the feed has no figure.
export function weeklyDemand(usage) {
  const gallons = Number(usage?.week_gallons);
  return usage?.week_gallons != null && Number.isFinite(gallons) && gallons > 0 ? gallons : null;
}

// ── The five answers ─────────────────────────────────────────────────────────
//
// Each answer is a list of parts - plain text, or { n } for a number shown
// bold and counted up - so the component can render it without HTML strings.

const compactGallons = (gallons) =>
  gallons >= 1000000 ? `${(gallons / 1000000).toFixed(2)}M` : `${Math.round(gallons / 1000)}K`;

// The design's standing figure for Pump Station 3 - there is no pump feed yet.

// `counts`: the alert counts every view shares (alertCounts.js) - the numbers
// here are those, not counted again. `metersStatus`: the meter list's feed
// ("ready", "failed" or "loading"), when the page knows it.
export function buildAnswers({
  flowHealth,
  meters,
  systemHealth,
  systemHealthMeters,
  usage,
  counts,
  metersStatus = Array.isArray(meters) ? "ready" : "loading",
}) {
  // Loaded, not non-empty: a district with no mapped meters still gets its
  // answers, its leaks placed by address alone.
  const hasMeters = Array.isArray(meters);
  const leakIds = counts.leak.ids;
  // The leaks' clusters (leakClusters) - Hotspots and Diagnosis report the
  // largest - once the leaks and the meter list are in.
  const leakGroups = counts.leak.status === "ready" && hasMeters ? leakClusters(leakIds, flowHealth, meters) : null;
  const topCluster = leakGroups?.clusters[0] || null;
  const answers = {};
  // A count, counted up - or "--" while its feed loads or after it failed,
  // never a 0 it does not know.
  const num = (part) => (part.count == null ? { b: "--" } : { n: part.count });

  // Q1 - what is happening
  if ([counts.leak, counts.anomaly, counts.backflow, counts.noflow].some((part) => part.status !== "loading")) {
    answers.q1 = {
      parts: [
        num(counts.leak), " active leaks, ",
        num(counts.anomaly), " anomalies, ",
        num(counts.backflow), " backflow and ",
        num(counts.noflow), " silent meters — surfaced live, not at month-end.",
      ],
      chips: [
        [num(counts.leak), " leaks"],
        [num(counts.anomaly), " anomalies"],
        [num(counts.noflow), " silent"],
      ],
    };
  }

  // Q2 - where (once leaks, anomalies, backflow and the meter list are all
  // in). Its flagged sites are the flagged meters the map can show - what
  // "Locate on map" rings; one with no map point is counted apart.
  const onMap = flaggedOnMap(counts, metersStatus, meterPointKeys(meters));
  if (onMap.status === "failed") {
    answers.q2 = {
      parts: [`Flagged sites are unavailable: the ${listText(onMap.failed)} data did not load.`],
      chips: [[{ b: "--" }, " flagged sites"]],
    };
  } else if (onMap.status === "ready") {
    const sites = onMap.located.length;
    const unmapped = onMap.ids.length - sites;
    const chips = [
      [{ n: sites }, " flagged sites"],
      ...(unmapped ? [[{ n: unmapped }, " not on map"]] : []),
      [{ n: 2 }, " plants online"],
    ];
    if (topCluster) {
      const more = leakGroups.clusters.length - 1;
      answers.q2 = {
        parts: [
          { n: topCluster.count },
          ` leaks cluster ${clusterWhere(topCluster)}; 2 treatment plants online and mapped.`,
        ],
        chips: [
          [{ n: topCluster.count }, " in cluster"],
          ...(more ? [[{ n: more }, ` more cluster${more === 1 ? "" : "s"}`]] : []),
          ...chips,
        ],
      };
    } else if (!onMap.ids.length) {
      answers.q2 = { parts: ["No flagged sites in the latest data; 2 treatment plants online and mapped."], chips };
    } else {
      answers.q2 = {
        parts: unmapped
          ? [
              "Alerts span ", { n: sites }, " sites on the map, and ", { n: unmapped },
              ` flagged meter${unmapped === 1 ? " has" : "s have"} no location; 2 treatment plants online and mapped.`,
            ]
          : ["Alerts span ", { n: sites }, " sites across the district; 2 treatment plants online and mapped."],
        chips,
      };
    }
  }

  // Q3 - why: leaks close together point at a shared main
  if (leakGroups) {
    const weakest = getWeakestHealthLayer(systemHealth, systemHealthMeters);
    const weakestParts = weakest
      ? [`. Weakest data layer: ${weakest.name.toLowerCase()} at `, { n: Math.round(weakest.pct) }, "%"]
      : [];
    const weakestChip = weakest ? [[`${weakest.name} `, { b: `${Math.round(weakest.pct)}%` }]] : [];
    if (topCluster) {
      // No confidence percentage: the one shown here was a fixed formula of
      // the cluster size (52 + 6 per leak, capped at 94), not a validated
      // score. The card states what is observed - how many leaks are close
      // together, and how close - and frames the shared main as something to
      // check.
      answers.q3 = {
        parts: [
          { n: topCluster.count }, " of ", { n: leakIds.length },
          ` leaks are ${clusterWhere(topCluster)} — check for a shared distribution main before treating them as separate breaks`,
          ...weakestParts, ".",
        ],
        chips: [
          [
            { b: `${topCluster.count} of ${leakIds.length}` },
            topCluster.span < 1 ? " leaks at one place" : ` leaks within ${formatSpan(topCluster.span)}`,
          ],
          ...weakestChip,
        ],
      };
    } else {
      // Same distinction as the Leak pattern diagnosis row: no two leaks
      // close together only means something for the leaks that could be
      // placed. With none placed, "dispersed" would be read off missing data,
      // and the cause of a dispersed leak is not in this feed either way.
      const placed = unclusteredText(leakGroups);
      answers.q3 = {
        parts: !leakIds.length
          ? ["No active leaks are flagged, so there is nothing to cluster", ...weakestParts, "."]
          : placed
            ? [
                { n: leakIds.length },
                ` leaks are flagged; ${placed} — no clustering in this data, and no cause established`,
                ...weakestParts, ".",
              ]
            : [
                { n: leakIds.length },
                " leaks are flagged; none has a location or an address, so clustering cannot be assessed",
                ...weakestParts, ".",
              ],
        chips: weakestChip.length ? weakestChip : [["inputs loading"]],
      };
    }
  }

  // Q4 - what to do: one crew per top leak, capped at five
  if (flowHealth && counts.leak.status === "ready") {
    const leakRecords = getFlowRecords(flowHealth, "leak");
    const dispatches = Math.min(5, leakRecords.length);
    const exposure = formatCompactCurrency(
      leakRecords.reduce((sum, record) => sum + parseNumberText(record.total_price_for_loss), 0),
    );
    answers.q4 = {
      parts: [{ n: dispatches }, ` dispatches recommended, ranked by revenue impact — ${exposure} at stake.`],
      chips: [
        [{ n: dispatches }, " crews"],
        [{ b: exposure }, " exposure"],
      ],
    };
  }

  // Q5 - what next: a week of demand at the last seven days' rate.
  //
  // The "Pump Station 3 carries a 42% failure risk within 30 days" sentence
  // that used to close this answer has been removed. PS3_FAILURE_RISK was a
  // literal 42 in this file - no API serves pump telemetry (see FLAGGED_PUMPS
  // below), so nothing measured that risk, and nothing would update it. The
  // projection that remains is computed from the usage feed.
  if (usage) {
    const week = weeklyDemand(usage);
    answers.q5 =
      week != null
        ? {
            parts: [
              "7-day demand projects to ≈ ",
              week >= 1000000 ? { b: `${(week / 1000000).toFixed(2)}M gal` } : { n: Math.round(week / 1000), suffix: "K gal" },
              " at the last seven days' rate.",
            ],
            chips: [["7-day ", { b: compactGallons(week) }, " gal"]],
          }
        : { parts: ["No usage figure is available for the past week."], chips: [["usage unavailable"]] };
  }

  // Leaks plus anomalies - Live Status's first two counts.
  answers.badge =
    counts.leak.count != null && counts.anomaly.count != null ? counts.leak.count + counts.anomaly.count : null;
  return answers;
}

// ── Accountability ───────────────────────────────────────────────────────────
//
// The card's three tabs, each a `system` of apis/accountability. The daily
// values come from the live feed (useDashboardFeeds -> feeds.accountability).

export const ACCT_CATEGORIES = {
  total: { label: "Total", noun: "System" },
  potable: { label: "Potable", noun: "Potable" },
  non_potable: { label: "Non-Potable", noun: "Non-potable" },
};

// What the numbers say, and only that - the live feed carries no cause, so
// the insight names the pattern and the check that follows from it.
function accountabilityInsight(noun, avg, under, over, days) {
  const parts = [
    `${noun} accountability averaged ${avg}% over the last ${days} days — ${under} day(s) under 80% and ${over} over 110%.`,
  ];
  if (under) {
    parts.push(
      "Days under 80% mean more water went into the system than customers' meters recorded — check for leaks, main breaks and unbilled flushing.",
    );
  }
  if (over) {
    parts.push(
      "Days over 110% mean meters recorded more than was supplied — check for supply meters under-reading or late meter data.",
    );
  }
  if (!under && !over) parts.push("Every day was within the normal 80–110% range.");
  return parts.join(" ");
}

// null while the feed is loading; a summary with no days when it came back
// empty, so the card can say so rather than spin.
export function accountabilitySummary(catKey, feed) {
  if (!feed) return null;
  const category = ACCT_CATEGORIES[catKey];
  const days = (feed[catKey]?.days || []).filter((day) => Number.isFinite(day.value));
  if (!days.length) return { avg: null, under: 0, over: 0, range: "", alertDays: [], days, insight: "" };
  const avg = Math.round(days.reduce((sum, day) => sum + day.value, 0) / days.length);
  const under = days.filter((day) => day.value < 80).length;
  const over = days.filter((day) => day.value > 110).length;
  return {
    avg,
    under,
    over,
    days,
    range: `${days[0].date} – ${days[days.length - 1].date}`,
    alertDays: days.filter((day) => day.value < 80 || day.value > 110),
    insight: accountabilityInsight(category.noun, avg, under, over, days.length),
  };
}

// ── Pumps ────────────────────────────────────────────────────────────────────
// The design's two flagged pump assets - no API serves pump data yet.
export const FLAGGED_PUMPS = [
  { site: "Pump Station 3", detail: "42% failure probability within 30 days", tag: "Maintenance due" },
  { site: "West Plant · Booster P-4", detail: "Offline advisory — flagged for service", tag: "Service scheduled" },
];

// ── AI diagnosis ("why is it happening?") ────────────────────────────────────
//
// `anomalyHistory` is the flagged meters' daily history over the last
// ANOMALY_HISTORY_DAYS (apis/anoamaly_detection, per meter). It is only needed
// here, so it is fetched when the diagnosis is opened rather than with the
// page, in batches (meterHistory.js), for at most the DIAGNOSIS_HISTORY_LIMIT
// meters with the highest peaks: { rows (the meters' records so far), total
// (flagged), asked, loaded, failed (meters whose batch failed), loading }.
// The anomaly row says what it rests on - still loading, failed, or part of
// the meters (historyCoverage).
export const ANOMALY_HISTORY_DAYS = 30;

// Where each kind of diagnosis row comes from - opened when none of its meters
// can be placed on the map.
export const DIAGNOSIS_SOURCES = {
  // Each opened on the dashboard's own window and narrowed to the row's meters
  // (?meters=, read by MeterFilterBanner on every source page).
  // src: the page's source chip and Back (components/mw2/context.js).
  leak: { path: "/leak-flow-category", label: "Leak Flow", query: { range: "D", status: "leak", src: "mizu.diagnosis" } },
  anomaly: { path: "/totalflow", label: "Anomaly Detection", query: { tab: "anomaly", range: "3M", status: "anomaly", src: "mizu.diagnosis" } },
  health: { path: "/health", label: "System Health", query: { src: "mizu.diagnosis" } },
  noflow: { path: "/flow-health", label: "No Recent Flow", query: { status: "silent", src: "mizu.diagnosis" } },
};

// System Health's tab for each health layer.
const HEALTH_TABS = {
  Connectivity: "communicationStatus",
  "Data quality": "dataStatus",
  "Device health": "deviceStatus",
};

// The source page's URL for a set of meters, narrowed to them.
export function sourceUrl(source, meterIds) {
  if (!source) return null;
  const params = new URLSearchParams(source.query || {});
  const ids = [...new Set((meterIds || []).map(String))];
  if (ids.length) params.set("meters", ids.join(","));
  const query = params.toString();
  return query ? `${source.path}?${query}` : source.path;
}

// The diagnosis's findings - each row of buildDiagnosisRows is one of them
// (its `finding`) - and the dashboard feeds each needs, by useDashboardFeeds'
// keys: `feed`, its own, without which it cannot be made, and `waits`, the
// others it needs in hand. Leaks and flow health reach the dashboard merged,
// so each waits for the other; leak clusters place leaks by the meter list;
// silent meters are cross-checked against system health (and say so when it
// failed). `skipped` says what is missing when its feed fails, `none` what an
// empty one shows.
export const DIAGNOSIS_FINDINGS = [
  {
    key: "leak", label: "Leaks", color: "var(--red)", feed: "leak", waits: ["flowHealth", "meters"],
    skipped: "leak clustering is not assessed", none: "No active leaks are flagged.",
  },
  {
    key: "anomaly", label: "Anomalies", color: "var(--amber)", feed: "anomaly", waits: [],
    skipped: "usage against each meter's learned pattern is not assessed", none: "No meters are flagged anomalous.",
  },
  {
    key: "health", label: "System health", color: "var(--cyan)", feed: "systemHealthLive", waits: [],
    skipped: "the weakest data layer is not assessed", none: "System health reports no layer figures to compare.",
  },
  {
    key: "noflow", label: "Silent meters", color: "var(--blue)", feed: "flowHealth", waits: ["leak", "systemHealthLive"],
    skipped: "silent meters are not assessed", none: "No meters report a lack of recent flow.",
  },
];

// The feeds by name, as the notes say them: "the system health data".
export const FEED_LABELS = {
  leak: "leak",
  flowHealth: "flow health",
  anomaly: "anomaly",
  systemHealthLive: "system health",
  meters: "meter location",
};

// A feed's state, read as the alert counts read it (useDashboardFeeds'
// `errors` and `fetchedAt`): in, failed (it never answered) or still loading.
// A refresh that failed after data came in leaves it in, with that data.
export const feedState = (key, errors = {}, fetchedAt = {}) =>
  fetchedAt[key] ? "ready" : errors[key] ? "failed" : "loading";

// Each diagnosis finding with its `state` - "ready", "loading" or "failed"
// (its own feed never answered) - its own feed's name (`data`) and `error`,
// and the feeds it is still waiting for, by name (`waitingFor`).
export function diagnosisState(errors = {}, fetchedAt = {}) {
  return DIAGNOSIS_FINDINGS.map((finding) => {
    const own = feedState(finding.feed, errors, fetchedAt);
    const waitingFor = [finding.feed, ...finding.waits]
      .filter((key) => feedState(key, errors, fetchedAt) === "loading")
      .map((key) => FEED_LABELS[key]);
    return {
      ...finding,
      state: own === "failed" ? "failed" : waitingFor.length ? "loading" : "ready",
      data: FEED_LABELS[finding.feed],
      waitingFor,
      error: errors[finding.feed] || null,
    };
  });
}

// Silent meters that ALSO appear on a system_battery fault list. The lists are
// communication_data and device_data (exposed as communication_meter /
// device_meter); a meter can be on both, so `either` is the union rather than
// the sum.
//
// `available` is false when the System Health call did not return - its feed
// falls back to {} with no lists, where a successful call always carries the
// arrays even when they are empty. Without it, "no corroborating fault" and
// "we could not look" would read identically.
export function corroborateSilentMeters(noFlowIds, systemHealthMeters) {
  const lists = systemHealthMeters || {};
  const available =
    Array.isArray(lists.communication_meter) || Array.isArray(lists.device_meter);
  if (!available) {
    return { available: false, comm: 0, device: 0, either: 0 };
  }
  const commSet = new Set((lists.communication_meter || []).map(String));
  const deviceSet = new Set((lists.device_meter || []).map(String));
  let comm = 0;
  let device = 0;
  let either = 0;
  noFlowIds.forEach((id) => {
    const inComm = commSet.has(String(id));
    const inDevice = deviceSet.has(String(id));
    if (inComm) comm += 1;
    if (inDevice) device += 1;
    if (inComm || inDevice) either += 1;
  });
  return { available: true, comm, device, either };
}

// What part of the flagged meters' history the anomaly finding rests on, when
// not all of it - " The history of 60 more is still loading. The history of
// 20 could not be loaded. ..." - or "" when all of it.
export function historyCoverage({ total = 0, asked = 0, loaded = 0, failed = 0 }) {
  const notes = [];
  const pending = asked - loaded - failed;
  if (pending > 0) notes.push(`The history of ${pending} more is still loading.`);
  if (failed > 0) notes.push(`The history of ${failed} could not be loaded.`);
  if (total > asked) {
    notes.push(
      `The other ${total - asked} flagged meters, with lower peaks, were not looked up: the diagnosis reads the history of at most ${asked}.`,
    );
  }
  return notes.length ? ` ${notes.join(" ")}` : "";
}

export function buildDiagnosisRows({
  flowHealth,
  anomaly,
  meters,
  systemHealth,
  systemHealthMeters,
  anomalyHistory,
}) {
  const rows = [];
  const leakIds = getFlowMeterIds(flowHealth, "leak");
  const leakGroups = leakClusters(leakIds, flowHealth, meters);
  const top = leakGroups.clusters[0];

  if (top) {
    const more = leakGroups.clusters.length - 1;
    rows.push({
      color: "var(--red)",
      title: "Leak cluster inference",
      finding: "leak",
      // The leaks of that cluster, not every leak.
      meterIds: top.ids,
      alertType: "leak",
      setLabel: top.street ? `leaks near ${titleCaseStreet(top.street)}` : "clustered leaks",
      source: DIAGNOSIS_SOURCES.leak,
      // Observation, not a scored probability - see buildAnswers q3. That the
      // leaks are close together is in the data; a shared CAUSE is not, so it
      // is named as something to check rather than something found.
      text: `${top.count} of ${leakIds.length} active leaks are ${clusterWhere(top)}. Being close together is an observation, not a confirmed common cause — inspecting the distribution main there would establish whether one exists.${more ? ` ${more} more cluster${more === 1 ? " is" : "s are"} flagged.` : ""}`,
    });
  } else if (leakIds.length) {
    // No two leaks close together - but that is only meaningful for the leaks
    // that could be placed. With none placed, the absence of a cluster is
    // absence of evidence, and saying "dispersed" would invent it.
    const placed = unclusteredText(leakGroups);
    rows.push({
      color: "var(--red)",
      title: "Leak pattern",
      finding: "leak",
      meterIds: leakIds,
      alertType: "leak",
      setLabel: "active leaks",
      source: DIAGNOSIS_SOURCES.leak,
      text: placed
        ? `${leakIds.length} active leaks are flagged; ${placed}. That shows no clustering in this data — it does not establish a cause for any individual leak.`
        : `${leakIds.length} active leaks are flagged. None has a location or an address in this data, so whether they cluster cannot be assessed here.`,
    });
  }

  // How far over their modelled threshold the flagged days ran, on average -
  // over the meters whose history has come in. Said with how much that is
  // when not every flagged meter's (historyCoverage).
  const history = anomalyHistory || {};
  let ratioSum = 0;
  let ratioCount = 0;
  (Array.isArray(history.rows) ? history.rows : []).forEach((record) => {
    (Array.isArray(record?.data) ? record.data : []).forEach((point) => {
      const threshold = Number(point.threshold_value) || 0;
      if (Number(point.anomaly) === 1 && threshold > 0) {
        ratioSum += (Number(point.total_daily_flow) || 0) / threshold;
        ratioCount += 1;
      }
    });
  });
  const anomalyIds = getAnomalyMeterIds(anomaly);
  // Only once some history is in: with none, the row says why instead.
  const coverage = history.loaded ? historyCoverage(history) : "";
  if (ratioCount) {
    rows.push({
      color: "var(--amber)",
      title: "Anomaly vs typical usage",
      finding: "anomaly",
      meterIds: anomalyIds,
      alertType: "anomaly",
      setLabel: "anomalous meters",
      source: DIAGNOSIS_SOURCES.anomaly,
      // The ratio is measured; what produced it is not. A meter reading high
      // against its model looks identical whether the water is really flowing
      // or the register is faulty, so neither is asserted.
      text: `Across ${history.loaded} anomalous meters, flagged days in the last ${ANOMALY_HISTORY_DAYS} days ran ${(ratioSum / ratioCount).toFixed(1)}× their modeled demand threshold (${ratioCount.toLocaleString()} flagged meter-days).${coverage} The ratio shows how far above the model the flow ran; it does not distinguish real consumption from a metering fault.`,
    });
  } else if (anomalyIds.length) {
    const why = history.loaded
      ? "their history carries no threshold to compare against."
      : history.failed && !history.loading
        ? "per-meter history could not be loaded."
        : "per-meter history is still loading.";
    rows.push({
      color: "var(--amber)",
      title: "Anomaly vs typical usage",
      finding: "anomaly",
      meterIds: anomalyIds,
      alertType: "anomaly",
      setLabel: "anomalous meters",
      source: DIAGNOSIS_SOURCES.anomaly,
      text: `${anomalyIds.length} meters are flagged for usage outside their learned daily pattern; ${why}${coverage}`,
    });
  }

  const weakest = getWeakestHealthLayer(systemHealth, systemHealthMeters);
  if (weakest) {
    rows.push({
      color: "var(--cyan)",
      title: "Weakest health layer",
      finding: "health",
      meterIds: weakest.ids,
      // Health has no alert overlay on the map; the selection ring marks them.
      alertType: null,
      setLabel: `meters affecting ${weakest.name.toLowerCase()}`,
      // On the tab of that layer.
      source: { ...DIAGNOSIS_SOURCES.health, query: { tab: HEALTH_TABS[weakest.name] || "dataStatus" } },
      text: `${weakest.name} is the weakest system layer at ${Math.round(weakest.pct)}% (${weakest.meters} meters affected). The other findings on this page rely on this data, so they are only as reliable as it is.`,
    });
  }

  const noFlowIds = getFlowMeterIds(flowHealth, "no_recent_flow");
  if (noFlowIds.length) {
    // Vacancy, stuck registers and lost endpoints were previously asserted as
    // "a mix" of the causes. None of the three is in this data. What IS
    // checkable is whether a silent meter also appears on a System Health
    // fault list, so that is what is reported.
    const corroborated = corroborateSilentMeters(noFlowIds, systemHealthMeters);
    let text;
    if (!corroborated.available) {
      text = `${noFlowIds.length} meters report no recent flow. System Health data did not load, so none of them could be cross-checked against communication or device faults.`;
    } else if (corroborated.either === 0) {
      text = `${noFlowIds.length} meters report no recent flow, and none appears on a System Health communication or device fault list. No fault corroborates the silence in this data, which does not rule one out.`;
    } else {
      const remaining = noFlowIds.length - corroborated.either;
      text = `${noFlowIds.length} meters report no recent flow. ${corroborated.either} of them also appear on a System Health fault list (${corroborated.comm} communication, ${corroborated.device} device; a meter can be on both). The other ${remaining} carry no corroborating fault in that data.`;
    }
    rows.push({
      color: "var(--blue)",
      title: "Silent meters",
      finding: "noflow",
      meterIds: noFlowIds,
      alertType: "noflow",
      setLabel: "silent meters",
      source: DIAGNOSIS_SOURCES.noflow,
      text,
    });
  }
  return rows;
}
